import React, { useState, useEffect } from 'react';
import {
  Card, Button, Table, Modal, Form, Input, Select, Space, Tag,
  Row, Col, DatePicker, Typography, Tooltip, Popconfirm,
  Divider, List,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined,
  FileSearchOutlined,
  CommentOutlined,
} from '@ant-design/icons';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { Inspection, InspectionFinding } from '../types';
import { staticApi } from '../utils/staticApi';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

type InspectionStatus = Inspection['status'];

const STATUS_CONFIG: Record<InspectionStatus, { color: string; label: string }> = {
  scheduled:   { color: 'blue',    label: 'Scheduled' },
  in_progress: { color: 'orange',  label: 'In Progress' },
  completed:   { color: 'green',   label: 'Completed' },
  cancelled:   { color: 'default', label: 'Cancelled' },
};

const RESULT_CONFIG: Record<string, { color: string; label: string }> = {
  PASS:        { color: 'green',  label: 'Pass' },
  FAIL:        { color: 'red',    label: 'Fail' },
  CONDITIONAL: { color: 'orange', label: 'Conditional' },
  PENDING:     { color: 'default', label: 'Pending' },
};

const INSPECTION_TYPES = [
  'Annual Safety', 'Fire Safety', 'Elevator', 'Plumbing', 'Electrical',
  'Roof', 'HVAC', 'Common Areas', 'Unit Inspection', 'Pool/Amenity', 'Other',
];

export const Inspections: React.FC = () => {
  const {
    inspections,
    fetchInspections,
    createInspection,
    updateInspection,
    completeInspection,
    addInspectionNote,
    deleteInspection,
  } = useCondoStore();
  const { currentCondo } = useAuthStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editingInspection, setEditingInspection] = useState<Inspection | null>(null);
  const [completingInspection, setCompletingInspection] = useState<Inspection | null>(null);
  const [notingInspection, setNotingInspection] = useState<Inspection | null>(null);
  const [viewingInspection, setViewingInspection] = useState<Inspection | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [form] = Form.useForm();
  const [completeForm] = Form.useForm();
  const [noteForm] = Form.useForm();

  useEffect(() => {
    if (currentCondo?.id) fetchInspections(currentCondo.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchInspections is a stable zustand action
  }, [currentCondo?.id]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = {
    scheduled:  inspections.filter(i => i.status === 'scheduled').length,
    inProgress: inspections.filter(i => i.status === 'in_progress').length,
    completed:  inspections.filter(i => i.status === 'completed').length,
    followUp:   inspections.filter(i => i.followUpRequired && i.status !== 'completed').length,
  };

  const filtered = statusFilter === 'ALL'
    ? inspections
    : inspections.filter(i => i.status === statusFilter);

  // ── CRUD handlers ────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingInspection(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEdit = (inspection: Inspection) => {
    setEditingInspection(inspection);
    form.setFieldsValue({
      type: inspection.type,
      title: inspection.title,
      unitNumber: inspection.unitNumber,
      inspector: inspection.inspector,
      scheduledDate: inspection.scheduledDate ? dayjs(inspection.scheduledDate) : null,
      followUpRequired: inspection.followUpRequired,
      followUpDate: inspection.followUpDate ? dayjs(inspection.followUpDate) : null,
      status: inspection.status,
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const vals = await form.validateFields();
      const payload = {
        ...vals,
        condoId: currentCondo!.id,
        scheduledDate: vals.scheduledDate ? vals.scheduledDate.format('YYYY-MM-DD') : undefined,
        followUpDate: vals.followUpDate ? vals.followUpDate.format('YYYY-MM-DD') : undefined,
        findings: editingInspection?.findings ?? [],
        notes: editingInspection?.notes ?? [],
      };

      if (editingInspection) {
        await updateInspection(editingInspection.id, payload);
        staticApi.message.success('Inspection updated');
      } else {
        await createInspection(payload);
        staticApi.message.success('Inspection created');
      }
      setIsModalOpen(false);
    } catch {
      // validation errors shown inline
    }
  };

  const openComplete = (inspection: Inspection) => {
    setCompletingInspection(inspection);
    completeForm.resetFields();
    setIsCompleteModalOpen(true);
  };

  const handleComplete = async () => {
    try {
      const vals = await completeForm.validateFields();
      await completeInspection(completingInspection!.id, vals.result);
      staticApi.message.success('Inspection marked as completed');
      setIsCompleteModalOpen(false);
    } catch {
      // validation errors shown inline
    }
  };

  const openNote = (inspection: Inspection) => {
    setNotingInspection(inspection);
    noteForm.resetFields();
    setIsNoteModalOpen(true);
  };

  const handleAddNote = async () => {
    try {
      const vals = await noteForm.validateFields();
      await addInspectionNote(notingInspection!.id, vals.text);
      staticApi.message.success('Note added');
      setIsNoteModalOpen(false);
    } catch {
      // validation errors shown inline
    }
  };

  const handleDelete = async (id: string) => {
    await deleteInspection(id);
    staticApi.message.success('Inspection deleted');
  };

  // ── Table columns ────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Inspection #',
      dataIndex: 'inspectionNumber',
      key: 'inspectionNumber',
      render: (v: string, record: Inspection) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => { setViewingInspection(record); setIsDetailModalOpen(true); }}>
          {v}
        </Button>
      ),
    },
    {
      title: 'Type / Title',
      key: 'typeTitle',
      render: (_: any, r: Inspection) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.title}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.type}</Text>
        </Space>
      ),
    },
    {
      title: 'Unit',
      dataIndex: 'unitNumber',
      key: 'unitNumber',
      render: (v: string) => v || <Text type="secondary">Common</Text>,
    },
    {
      title: 'Scheduled',
      dataIndex: 'scheduledDate',
      key: 'scheduledDate',
      render: (v: string) => v ? dayjs(v).format('MMM D, YYYY') : '—',
    },
    {
      title: 'Inspector',
      dataIndex: 'inspector',
      key: 'inspector',
      render: (v: string) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: InspectionStatus) => {
        const cfg = STATUS_CONFIG[s] ?? { color: 'default', label: s };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
      render: (r: string) => {
        if (!r) return <Text type="secondary">—</Text>;
        const cfg = RESULT_CONFIG[r] ?? { color: 'default', label: r };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Follow-Up',
      dataIndex: 'followUpRequired',
      key: 'followUpRequired',
      render: (v: boolean, r: Inspection) =>
        v ? <Tag color="orange">Required {r.followUpDate ? dayjs(r.followUpDate).format('MMM D') : ''}</Tag>
          : <Text type="secondary">—</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Inspection) => (
        <Space>
          {(record.status === 'scheduled' || record.status === 'in_progress') && (
            <Tooltip title="Mark Complete">
              <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                onClick={() => openComplete(record)} />
            </Tooltip>
          )}
          <Tooltip title="Add Note">
            <Button size="small" icon={<CommentOutlined />} onClick={() => openNote(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm title="Delete this inspection?" onConfirm={() => handleDelete(record.id)} okText="Delete" okType="danger">
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <FileSearchOutlined style={{ marginRight: 8 }} />
            Inspection Tracking
          </Title>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New Inspection
          </Button>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {[
          { label: 'Scheduled', value: stats.scheduled, color: '#1890ff', bg: '#e6f7ff' },
          { label: 'In Progress', value: stats.inProgress, color: '#fa8c16', bg: '#fff7e6' },
          { label: 'Completed', value: stats.completed, color: '#52c41a', bg: '#f6ffed' },
          { label: 'Follow-Up Required', value: stats.followUp, color: '#f5222d', bg: '#fff1f0' },
        ].map(c => (
          <Col span={6} key={c.label}>
            <Card style={{ background: c.bg, border: `1px solid ${c.color}33` }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: c.color }}>{c.value}</div>
                <div style={{ color: '#595959' }}>{c.label}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filter */}
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <Text strong>Filter:</Text>
          {['ALL', 'scheduled', 'in_progress', 'completed', 'cancelled'].map(s => (
            <Button
              key={s}
              type={statusFilter === s ? 'primary' : 'default'}
              size="small"
              onClick={() => setStatusFilter(s)}
            >
              {s === 'ALL' ? 'All' : STATUS_CONFIG[s as InspectionStatus]?.label ?? s}
            </Button>
          ))}
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 15 }}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={editingInspection ? 'Edit Inspection' : 'New Inspection'}
        open={isModalOpen}
        onOk={handleSave}
        onCancel={() => setIsModalOpen(false)}
        okText="Save"
        width={620}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="type" label="Inspection Type" rules={[{ required: true }]}>
                <Select placeholder="Select type">
                  {INSPECTION_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                <Input placeholder="e.g. Annual Fire Safety Inspection" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="unitNumber" label="Unit Number (leave blank for common areas)">
                <Input placeholder="e.g. 301" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="inspector" label="Inspector">
                <Input placeholder="Inspector name or company" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="scheduledDate" label="Scheduled Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select placeholder="Select status" defaultValue="scheduled">
                  <Option value="scheduled">Scheduled</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="followUpRequired" label="Follow-Up Required" initialValue={false}>
                <Select>
                  <Option value={false}>No</Option>
                  <Option value={true}>Yes</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="followUpDate" label="Follow-Up Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Complete Inspection Modal */}
      <Modal
        title="Complete Inspection"
        open={isCompleteModalOpen}
        onOk={handleComplete}
        onCancel={() => setIsCompleteModalOpen(false)}
        okText="Mark Complete"
        okButtonProps={{ type: 'primary' }}
      >
        <p>Recording completion for: <strong>{completingInspection?.title}</strong></p>
        <Form form={completeForm} layout="vertical">
          <Form.Item name="result" label="Inspection Result" rules={[{ required: true, message: 'Please select a result' }]}>
            <Select placeholder="Select result">
              <Option value="PASS"><Tag color="green">Pass</Tag></Option>
              <Option value="FAIL"><Tag color="red">Fail</Tag></Option>
              <Option value="CONDITIONAL"><Tag color="orange">Conditional</Tag></Option>
              <Option value="PENDING"><Tag>Pending</Tag></Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Note Modal */}
      <Modal
        title="Add Note"
        open={isNoteModalOpen}
        onOk={handleAddNote}
        onCancel={() => setIsNoteModalOpen(false)}
        okText="Add Note"
      >
        <Form form={noteForm} layout="vertical">
          <Form.Item name="text" label="Note" rules={[{ required: true, message: 'Note cannot be empty' }]}>
            <TextArea rows={4} placeholder="Enter inspection note..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail / View Modal */}
      <Modal
        title={`Inspection: ${viewingInspection?.inspectionNumber}`}
        open={isDetailModalOpen}
        onCancel={() => setIsDetailModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsDetailModalOpen(false)}>Close</Button>,
        ]}
        width={700}
      >
        {viewingInspection && (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">Type</Text>
                <div>{viewingInspection.type}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Title</Text>
                <div>{viewingInspection.title}</div>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col span={12}>
                <Text type="secondary">Status</Text>
                <div>
                  <Tag color={STATUS_CONFIG[viewingInspection.status]?.color}>
                    {STATUS_CONFIG[viewingInspection.status]?.label}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Result</Text>
                <div>
                  {viewingInspection.result
                    ? <Tag color={RESULT_CONFIG[viewingInspection.result]?.color}>{RESULT_CONFIG[viewingInspection.result]?.label}</Tag>
                    : '—'}
                </div>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col span={12}>
                <Text type="secondary">Inspector</Text>
                <div>{viewingInspection.inspector || '—'}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Unit</Text>
                <div>{viewingInspection.unitNumber || 'Common Area'}</div>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col span={12}>
                <Text type="secondary">Scheduled</Text>
                <div>{viewingInspection.scheduledDate ? dayjs(viewingInspection.scheduledDate).format('MMM D, YYYY') : '—'}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Completed</Text>
                <div>{viewingInspection.completedDate ? dayjs(viewingInspection.completedDate).format('MMM D, YYYY') : '—'}</div>
              </Col>
            </Row>

            {/* Findings */}
            {viewingInspection.findings && viewingInspection.findings.length > 0 && (
              <>
                <Divider>Findings</Divider>
                <List
                  size="small"
                  dataSource={viewingInspection.findings}
                  renderItem={(f: InspectionFinding) => (
                    <List.Item>
                      <Space>
                        <Tag color={f.severity === 'pass' ? 'green' : f.severity === 'critical' ? 'red' : f.severity === 'major' ? 'orange' : 'gold'}>
                          {f.severity?.toUpperCase()}
                        </Tag>
                        <Text strong>{f.area}</Text>
                        <Text>{f.description}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </>
            )}

            {/* Notes */}
            {viewingInspection.notes && viewingInspection.notes.length > 0 && (
              <>
                <Divider>Activity Notes</Divider>
                <List
                  size="small"
                  dataSource={viewingInspection.notes}
                  renderItem={(n: any) => (
                    <List.Item>
                      <Space direction="vertical" size={0}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {n.author} — {n.date ? dayjs(n.date).format('MMM D, YYYY h:mm A') : ''}
                        </Text>
                        <Text>{n.text}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};
